import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, BookOpen } from "lucide-react";
import CourseContentForm from "./CourseContentForm";
import {
  getCourseContentsAPI,
  getCourseContentByIdAPI,
  deleteCourseContentAPI,
} from "../services/apicollection";

const Course_ContentManagement = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const controller = new AbortController();

  const fetchContents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCourseContentsAPI({ signal: controller.signal });
      if (res?.status !== false && Array.isArray(res?.data)) {
        setContents(res.data);
      } else {
        setContents([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load course contents");
      setContents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContents();
    return () => {
      controller.abort();
    };
  }, [fetchContents]);

  const handleCreate = () => {
    setEditingContent(null);
    setShowForm(true);
  };

  const handleEdit = async (content) => {
    try {
      const res = await getCourseContentByIdAPI(content._id);
      if (res?.status !== false && res?.data) {
        setEditingContent(res.data);
        setShowForm(true);
      } else {
        setError("Failed to load content");
      }
    } catch (err) {
      setError(err.message || "Failed to load content");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this course content?")) return;
    setDeletingId(id);
    try {
      const res = await deleteCourseContentAPI(id);
      if (res?.status === false) throw new Error(res.message);
      await fetchContents();
    } catch (err) {
      alert(err.message || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingContent(null);
    fetchContents();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingContent(null);
  };

  if (showForm) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-200">
          {editingContent ? "Edit Course Content" : "New Course Content"}
        </h2>
        <CourseContentForm
          initialData={editingContent}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Course Content</h2>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          <Plus size={16} />
          Add content
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : contents.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-16 text-center">
          <BookOpen size={40} className="text-slate-300" />
          <p className="mt-2 text-sm text-slate-400">No course content yet</p>
          <button
            type="button"
            onClick={handleCreate}
            className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
          >
            Add your first course content
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e]">
          <div className="divide-y divide-[#1d3350]">
            {contents.map((c) => (
              <div
                key={c._id}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div>
                  <p className="font-medium text-slate-200">
                    {c.course?.courseName || "Unknown"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {c.course?.category || ""} • {c.topicOverview?.length || 0} | {c.course?.mode} |  {c.course?.programType}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(c)}
                    className="rounded p-2 text-slate-400 hover:bg-[#13233a]"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(c._id)}
                    disabled={deletingId === c._id}
                    className="rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title="Delete"
                  >
                    {deletingId === c._id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Course_ContentManagement;
